import Modal from 'flarum/common/components/Modal';
import app from 'flarum/forum/app';

/**
 * AskQuestionModal — 提问确认弹窗
 *
 * 流程：
 *   step=select → 用户勾选"AI 答疑"和/或"导师答疑"，实时显示费用
 *   step=confirm → 确认费用 + 输入问题内容 → 提交
 *   step=done    → 成功提示
 *
 * 说明：
 *   - 选中 AI 答疑 → 必然获得 AI 答复
 *   - 选中导师答疑 → {{hours}} 小时未答复则自动退款
 *   - 未绑定导师时，导师答疑复选框禁用
 */
export default class AskQuestionModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.selectedText = this.attrs.selectedText || '';
    this.discussionId = this.attrs.discussionId || null;
    this.postId = this.attrs.postId || null;
    this.articleId = this.attrs.articleId || null;

    // 费用
    this.aiCost = Number(app.forum.attribute('taroAskAiCost') || 5);
    this.humanCost = Number(app.forum.attribute('taroAskHumanCost') || 50);
    this.humanResponseHours = Number(app.forum.attribute('taroAskHumanResponseHours') || 72);

    // 当前用户的导师（null = 未绑定）
    this.myMentor = app.forum.attribute('taroAskMyMentor') || null;

    // 用户选项
    this.needAi = false;
    this.needHuman = false;

    // 问题内容
    this.question = '';

    this.balance = this.getUserBalance();
    this.loading = false;
    this.error = '';
    this.step = 'select'; // 'select' | 'confirm' | 'submitting' | 'done'
  }

  getUserBalance() {
    // 优先从 forum attribute 读（每次 ForumSerializer 调用都拿到最新值）
    const forumBalance = app.forum.attribute('taroAskMyBalance');
    if (forumBalance && typeof forumBalance === 'object') {
      return Number(forumBalance.star_coin || 0);
    }
    // 回退：从 user 模型读（旧逻辑兼容）
    const user = app.session.user;
    if (!user) return 0;
    return Number(user.attribute('starCoinBalance') || user.attribute('credits.star_coin') || 0);
  }

  get totalCost() {
    return (this.needAi ? this.aiCost : 0) + (this.needHuman ? this.humanCost : 0);
  }

  get hasEnoughBalance() {
    return this.balance >= this.totalCost;
  }

  get canProceed() {
    return (this.needAi || this.needHuman) && this.hasEnoughBalance;
  }

  /** 是否有可用导师 */
  get hasMentor() {
    return this.myMentor !== null;
  }

  /** 切换导师答疑 — 未绑定时禁用 */
  toggleHuman(e) {
    if (!this.hasMentor) {
      // 阻止勾选
      e.target.checked = false;
      return;
    }
    this.needHuman = e.target.checked;
    this.error = '';
    m.redraw();
  }

  /** 🔴 阻止 <form> 提交导致页面导航 → modal 关闭 */
  onsubmit(e) {
    e.preventDefault();
  }

  className() {
    return 'AskQuestionModal Modal--small';
  }

  title() {
    return app.translator.trans('taro-ask.forum.ask_title');
  }

  content() {
    switch (this.step) {
      case 'select':
        return this.renderSelectStep();
      case 'confirm':
        return this.renderConfirmStep();
      case 'submitting':
        return this.renderSubmittingStep();
      case 'done':
        return this.renderDoneStep();
    }
  }

  renderSelectStep() {
    return (
      <div className="Modal-body">
        <h4>{app.translator.trans('taro-ask.forum.option_title')}</h4>

        {/* AI 答疑 */}
        <div className="AskQuestionModal-option AskQuestionModal-option--ai">
          <label>
            <input
              type="checkbox"
              checked={this.needAi}
              onchange={(e) => { this.needAi = e.target.checked; this.error = ''; }}
            />
            <span className="AskQuestionModal-optionName">
              <i className="fas fa-robot"></i>{' '}
              {app.translator.trans('taro-ask.forum.option_ai_label')}
            </span>
            <span className="AskQuestionModal-optionFee">{this.aiCost} 星币</span>
          </label>
          <p className="AskQuestionModal-optionDesc">
            <i className="fas fa-check-circle"></i>{' '}
            {app.translator.trans('taro-ask.forum.option_ai_desc')}
          </p>
        </div>

        {/* 导师答疑 */}
        <div
          className={
            'AskQuestionModal-option AskQuestionModal-option--mentor' +
            (this.hasMentor ? '' : ' AskQuestionModal-option--disabled')
          }
        >
          <label>
            <input
              type="checkbox"
              checked={this.needHuman}
              disabled={!this.hasMentor}
              onchange={(e) => this.toggleHuman(e)}
            />
            <span className="AskQuestionModal-optionName">
              <i className="fas fa-user-tie"></i>{' '}
              {app.translator.trans('taro-ask.forum.option_mentor_label')}
            </span>
            <span className="AskQuestionModal-optionFee">{this.humanCost} 星币</span>
          </label>
          <p className="AskQuestionModal-optionDesc">
            {this.hasMentor ? (
              <>
                <i className="fas fa-user"></i>{' '}
                导师：<strong>{this.myMentor.displayName || this.myMentor.username}</strong>
                {' · '}
                <i className="fas fa-clock"></i>{' '}
                {app.translator.trans('taro-ask.forum.option_mentor_desc', {
                  hours: this.humanResponseHours,
                })}
              </>
            ) : (
              <>
                <i className="fas fa-info-circle"></i>{' '}
                {app.translator.trans('taro-ask.forum.no_mentor')}
              </>
            )}
          </p>
        </div>

        {/* 费用栏 */}
        <div className="AskQuestionModal-feeBox">
          <div className="AskQuestionModal-feeRow">
            <span>{app.translator.trans('taro-ask.forum.balance_label')}</span>
            <span className={this.hasEnoughBalance ? 'AskQuestionModal-balance-ok' : 'AskQuestionModal-balance-low'}>
              {this.balance} 星币
            </span>
          </div>
          {(this.needAi || this.needHuman) && (
            <div className="AskQuestionModal-feeRow AskQuestionModal-feeRow--total">
              <span>{app.translator.trans('taro-ask.forum.total_cost_label')}</span>
              <span>{this.totalCost} 星币</span>
            </div>
          )}
          {!this.hasEnoughBalance && (this.needAi || this.needHuman) && (
            <p className="AskQuestionModal-error">{app.translator.trans('taro-ask.forum.balance_insufficient')}</p>
          )}
        </div>

        {this.error && <p className="AskQuestionModal-error">{this.error}</p>}

        <div className="AskQuestionModal-actions">
          <button
            type="button"
            className="Button Button--primary"
            disabled={!this.canProceed}
            onclick={() => this.goToConfirm()}
          >
            下一步
            <i className="fas fa-arrow-right"></i>
          </button>
          {' '}
          <button type="button" className="Button" onclick={() => this.hide()}>
            {app.translator.trans('taro-ask.forum.cancel')}
          </button>
        </div>
      </div>
    );
  }

  renderConfirmStep() {
    return (
      <div className="Modal-body">
        <h4>问题内容</h4>

        <div className="AskQuestionModal-selectedBox">
          <strong>选中文字：</strong>
          <p>{this.selectedText.substring(0, 300)}{this.selectedText.length > 300 ? '…' : ''}</p>
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('taro-ask.forum.question_label')}</label>
          <textarea
            className="FormControl"
            rows={4}
            value={this.question}
            oninput={(e) => { this.question = e.target.value; this.error = ''; }}
            placeholder={app.translator.trans('taro-ask.forum.question_placeholder')}
          />
        </div>

        <div className="AskQuestionModal-summary">
          <div className="AskQuestionModal-summaryTitle">将扣除 <strong>{this.totalCost}</strong> 星币</div>
          <ul className="AskQuestionModal-summaryList">
            {this.needAi && (
              <li>
                <i className="fas fa-robot"></i>
                AI 答疑 — {this.aiCost} 星币
                <span className="AskQuestionModal-tag">必得答复</span>
              </li>
            )}
            {this.needHuman && this.myMentor && (
              <li>
                <i className="fas fa-user-tie"></i>
                导师：{this.myMentor.displayName || this.myMentor.username}
                {' — '}
                {this.humanCost} 星币
                <span className="AskQuestionModal-tag AskQuestionModal-tag--refund">
                  {this.humanResponseHours} 小时未答复自动退款
                </span>
              </li>
            )}
          </ul>
        </div>

        {this.error && <p className="AskQuestionModal-error">{this.error}</p>}

        <div className="AskQuestionModal-actions">
          <button
            type="button"
            className="Button Button--primary"
            disabled={this.loading || !this.question.trim()}
            onclick={() => this.submit()}
          >
            {this.loading
              ? [<i className="fas fa-spinner fa-pulse" />, ' ']
              : null}
            确认支付 {this.totalCost} 星币
          </button>
          {' '}
          <button
            type="button"
            className="Button"
            disabled={this.loading}
            onclick={() => { this.step = 'select'; }}
          >
            返回上一步
          </button>
        </div>
      </div>
    );
  }

  renderSubmittingStep() {
    return (
      <div className="Modal-body">
        <div className="AskQuestionModal-success">
          <i className="fas fa-spinner fa-pulse" style="font-size: 48px; color: var(--primary-color, #4d6bfe);"></i>
          <p>提交中…</p>
        </div>
      </div>
    );
  }

  renderDoneStep() {
    return (
      <div className="Modal-body">
        <div className="AskQuestionModal-success">
          <i className="fas fa-check-circle" style="font-size: 48px; color: var(--primary-color, #4d6bfe);"></i>
          <p>{app.translator.trans('taro-ask.forum.submit_success')}</p>
          <p style="font-size: 13px; color: #888;">扣费 {this.totalCost} 星币</p>
        </div>
      </div>
    );
  }

  goToConfirm() {
    if (!this.needAi && !this.needHuman) {
      this.error = app.translator.trans('taro-ask.forum.need_select_one');
      return;
    }
    if (!this.hasEnoughBalance) {
      this.error = app.translator.trans('taro-ask.forum.balance_insufficient');
      return;
    }
    this.step = 'confirm';
    this.error = '';
    m.redraw();
  }

  submit() {
    if (!this.question.trim()) return;
    if (!this.needAi && !this.needHuman) return;

    this.loading = true;
    this.error = '';
    this.step = 'submitting';
    m.redraw();

    const body = {
      data: {
        attributes: {
          discussionId: this.discussionId || null,
          postId: this.postId || null,
          articleId: this.articleId || null,
          highlightedText: this.selectedText,
          domAnchor: '',
          question: this.question.trim(),
          needAi: this.needAi,
          needHuman: this.needHuman,
          needMentor: this.needHuman,
        },
      },
    };

    app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/taro/ask-questions',
        body,
      })
      .then(() => {
        this.step = 'done';
        this.loading = false;
        m.redraw();
        setTimeout(() => this.hide(), 3000);
      })
      .catch((err) => {
        this.loading = false;
        this.step = 'confirm';
        const msg = err && err.response && err.response.errors
          ? err.response.errors[0]?.detail
          : null;
        this.error = msg || app.translator.trans('taro-ask.forum.submit_error');
        m.redraw();
      });
  }
}